/**
 * How well the arena AI plays, on the two dials a settings screen exposes. Both are 0…1.
 * `accuracy` is the quality of what the AI knows and decides: aiming slip, leading a moving
 * target, noticing incoming shots, using cover, playing carefully when hurt. `speed` is how fast
 * it acts on that: reaction delay, turn rate, running speed and attack cadence.
 */
export interface ArenaAiSkill {
  accuracy: number;
  speed: number;
}

export const DEFAULT_ARENA_SKILL: Readonly<ArenaAiSkill> = Object.freeze({
  accuracy: 0.6,
  speed: 0.6,
});

/** The concrete numbers a skill setting turns into; everything the behaviours read. */
export interface SkillProfile {
  /** Seconds between something happening and the AI seeing it. */
  reaction: number;
  /** How much of the enemy's motion the AI extrapolates across its reaction delay (0…1). */
  anticipation: number;
  /** Fraction of the fighter's run speed the AI uses. */
  moveScale: number;
  /** Radians per second the AI can turn its aim. */
  turnRate: number;
  /** Extra seconds of hesitation per attack once the weapon is ready. */
  cadence: number;
  /** Largest aiming slip in radians; the typical slip is well under half of it. */
  aimSpread: number;
  /** How much of the full lead on a moving target the AI applies (0…1). */
  lead: number;
  /** Chance that an incoming shot is noticed at all. */
  notice: number;
  /** Below this fraction of hit points (and behind the enemy's) the AI fights carefully. */
  carefulBelow: number;
  /** How far the AI goes for cover, metres; under one metre it never bothers. */
  coverRadius: number;
  /** Sideways weave while closing in on a shooter, as a fraction of the strafe. */
  weave: number;
  /** How much wider than the hit box the AI accepts its aim before it fires. */
  fireTolerance: number;
  /** How far the preferred shooting distance wanders, as a fraction. */
  rangeWobble: number;
  /** Preferred shooting distance, metres. */
  rangedDistance: number;
}

/**
 * The four standards of duelling opponent, as the aiming cone each is allowed and the share of a
 * fighter's speed each moves at. Everything else in the profile is still a curve over the two
 * dials; these two are the ones a player feels, so they are stated rather than derived.
 *
 * The cone is a half-angle: Novice puts its shots within twenty degrees of true and Master within
 * five. Movement is relative to the character's own themed combat speed; Expert uses that speed,
 * while the other levels range from half pace to a quarter faster.
 */
const AIM_DEGREES: readonly [level: number, degrees: number][] = [
  [0.3, 20],
  [0.6, 10],
  [0.8, 7],
  [1, 5],
];
const MOVE_SCALE: readonly [level: number, scale: number][] = [
  [0.35, 0.5],
  [0.6, 0.75],
  [0.8, 1],
  [1, 1.25],
];

/** Reads a stated table at a dial's setting, straight between the points it names. */
function interpolate(table: readonly [number, number][], at: number): number {
  const first = table[0]!;
  const last = table[table.length - 1]!;
  if (at <= first[0]) return first[1];
  if (at >= last[0]) return last[1];
  for (let i = 1; i < table.length; i++) {
    const [x1, y1] = table[i]!;
    const [x0, y0] = table[i - 1]!;
    if (at <= x1) return y0 + ((y1 - y0) * (at - x0)) / (x1 - x0);
  }
  return last[1];
}

export function skillProfile(skill: ArenaAiSkill): SkillProfile {
  const a = clamp01(skill.accuracy);
  const s = clamp01(skill.speed);
  return {
    reaction: 0.05 + 0.55 * (1 - s),
    anticipation: a,
    moveScale: interpolate(MOVE_SCALE, s),
    turnRate: 5 + 11 * s,
    cadence: 0.5 * (1 - s),
    aimSpread: (interpolate(AIM_DEGREES, a) * Math.PI) / 180,
    lead: clamp01((a - 0.3) / 0.7),
    notice: 0.25 + 0.75 * a,
    carefulBelow: 0.45 * a,
    coverRadius: 2.5 * a,
    weave: 0.6 * a,
    fireTolerance: 1 + 2 * (1 - a),
    rangeWobble: 0.4 * (1 - a),
    rangedDistance: 7.5,
  };
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.5;
}

/**
 * The duel opponent's strength in four steps, named the same four ways as the board AI beside it
 * so that one word means one standard of opponent wherever a player meets it.
 */
export type DuelLevel = 'novice' | 'fair' | 'expert' | 'master';

export interface DuelStatProfile {
  hitPoints: number;
  damage: number;
  attackInterval: number;
  effectChance: number;
  protection: number;
  powerGoal: number;
}

/**
 * The shared part of the difficulty ladder (`docs/09-balance.md`, "Combat levels"): hit points and
 * damage fitted on 2026-09-21 so that a character beats itself one level down in about 80 % of
 * mirror duels, Expert AI on both sides; the other dials step as authored. `powerGoal` is the
 * level's combat power against Expert under the acceptance curve, a ×2.9 step, which the Roll
 * mode turns into odds through `POWER_CURVE_EXPONENT` and the tools use as the rating goal.
 */
/**
 * The acceptance curve's exponent: a fighter of combat power P scores `1 / (1 + (goal/P)^1.3)`
 * against a goal, so the odds between two powers are their ratio to this power. The ladder's
 * ×2.9 step is the 80 % share under it.
 */
export const POWER_CURVE_EXPONENT = 1.3;

export const DUEL_STAT_PROFILES: Readonly<Record<DuelLevel, DuelStatProfile>> = Object.freeze({
  novice: {
    hitPoints: 0.763,
    damage: 0.673,
    attackInterval: 1.15,
    effectChance: 0.85,
    protection: 0.75,
    powerGoal: 0.1185,
  },
  fair: {
    hitPoints: 0.854,
    damage: 0.808,
    attackInterval: 1.05,
    effectChance: 0.95,
    protection: 0.9,
    powerGoal: 0.3443,
  },
  expert: {
    hitPoints: 1,
    damage: 1,
    attackInterval: 1,
    effectChance: 1,
    protection: 1,
    powerGoal: 1,
  },
  master: {
    hitPoints: 1.177,
    damage: 1.234,
    attackInterval: 0.95,
    effectChance: 1.05,
    protection: 1.1,
    powerGoal: 2.905,
  },
});

export const DUEL_LEVELS: readonly { id: DuelLevel; label: string; skill: ArenaAiSkill }[] = [
  { id: 'novice', label: 'Novice', skill: { accuracy: 0.3, speed: 0.35 } },
  { id: 'fair', label: 'Fair', skill: { accuracy: 0.7, speed: 0.6 } },
  { id: 'expert', label: 'Expert', skill: { accuracy: 0.8, speed: 0.8 } },
  { id: 'master', label: 'Master', skill: { accuracy: 1, speed: 1 } },
];

export function isDuelLevel(value: unknown): value is DuelLevel {
  return DUEL_LEVELS.some((d) => d.id === value);
}

/** The skill of a level; an unknown level plays fair. */
export function duelLevelSkill(level: DuelLevel | undefined): ArenaAiSkill {
  return (DUEL_LEVELS.find((d) => d.id === level) ?? DUEL_LEVELS[1]!).skill;
}

/**
 * The level whose authored skill lies nearest a custom one, both dials counted alike. The practice
 * opponent's sliders can sit anywhere; it fights with the stats of the step it is nearest, where
 * the exact match below gave everything off a step Expert's hit points and damage.
 */
export function nearestDuelLevel(skill: ArenaAiSkill): DuelLevel {
  let nearest = DUEL_LEVELS[0]!;
  for (const entry of DUEL_LEVELS)
    if (
      Math.hypot(entry.skill.accuracy - skill.accuracy, entry.skill.speed - skill.speed) <
      Math.hypot(nearest.skill.accuracy - skill.accuracy, nearest.skill.speed - skill.speed)
    )
      nearest = entry;
  return nearest.id;
}

/** Resolves one of the four authored skills back to its level; custom skills remain Expert stats. */
export function duelLevelForSkill(skill: ArenaAiSkill | undefined): DuelLevel {
  return (
    DUEL_LEVELS.find(
      (entry) => entry.skill.accuracy === skill?.accuracy && entry.skill.speed === skill?.speed,
    )?.id ?? 'expert'
  );
}
